"use client";

import React from "react";
import { Clock, DollarSign, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Time_Entries } from "@/src/gql/graphql";

import { EditableBillableRate } from "./EditableBillableRate";
import { computeTimeEntryRevenue, formatDateWorked } from "../time-entry-details-utils";

/**
 * "Time Entry Information" card — a two-column grid of date worked, hours, entry
 * type, inline-editable billable rate, computed revenue, and the entry-type
 * badge. Revenue derives from {@link computeTimeEntryRevenue}.
 */
export function TimeEntryInformationSection({ item }: { item: Time_Entries }) {
  const revenue = computeTimeEntryRevenue(item);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Time Entry Information</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Date Worked</label>
              <div className="flex items-center mt-1">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-card-foreground">
                  {formatDateWorked(item.date_worked)}
                </span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Hours</label>
              <div className="flex items-center mt-1">
                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-card-foreground">
                  {item.hours || 0} hours
                </span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Entry Type</label>
              <div className="mt-1">
                <Badge variant="outline">{item.entry_type || "Unknown"}</Badge>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Billable Rate</label>
              <EditableBillableRate entryId={item.id} rate={item.billable_rate ?? null} />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Revenue</label>
              <div className="flex items-center mt-1">
                <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-card-foreground">
                  ${revenue.toFixed(2)}
                </span>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Entry Type</label>
              <div className="mt-1">
                <Badge variant={item.entry_type === "billable" ? "default" : "secondary"}>
                  {item.entry_type || "billable"}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
